import React, { Children, isValidElement } from 'react'
import { Tabs } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import type { TabsProps } from 'react-bootstrap/Tabs'

// The one prop this component reads off its children. `isValidElement` alone narrows `props` to
// `unknown`, so the membership check needs the shape spelled out.
type TabChildProps = { eventKey?: string }

export type TabWithSearchParamsProps = {
  children?: any
  tabName?: string
  defaultActiveKey?: string
  sticky?: boolean
} & TabsProps

export const TabWithSearchParams = ({
  children,
  defaultActiveKey,
  tabName,
  sticky = false,
  ...props
}: TabWithSearchParamsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  tabName = tabName ?? 'tab'

  // react-bootstrap selects nothing and mounts no pane for an `activeKey` matching no child's
  // `eventKey`, so the page renders as a tab bar with no active tab above an empty body -- no
  // content, no empty state, no error. A hand-edited or stale URL is enough to reach that, so an
  // unrecognised value falls back to the default tab instead.
  //
  // The comparison is exact on purpose. Case-folding it so `credits` selected `Credits` would hide
  // the mismatch rather than surface it, and the keys are enum values the consumer controls.
  //
  // `Children.toArray` drops null/false/undefined children, so a tab a consumer renders
  // conditionally is simply absent from the key set -- the correct reading, since a pane that is
  // not rendered cannot be selected either.
  //
  // It flattens nested ARRAYS but does not descend into a Fragment, so the tabs must be direct
  // children of this component -- as they are at every call site today. Wrapping them in a fragment
  // would empty the key set and pin every consumer to its default tab.
  const paramValue = searchParams.get(tabName)
  const childKeys = Children.toArray(children)
    .filter((child): child is React.ReactElement<TabChildProps> => isValidElement(child))
    .map((child) => child.props.eventKey)
  // Also covers an absent param: `null` is never a child key, so it lands on the default exactly as
  // the previous `?? defaultActiveKey` did.
  const activeKey = childKeys.includes(paramValue) ? paramValue : defaultActiveKey

  const stickyClassName = sticky ? 'sticky-top' : ''
  const stickyStyle: React.CSSProperties = sticky
    ? {
        zIndex: 3,
        backgroundColor: 'var(--bs-body-bg)',
      }
    : {}

  return (
    <Tabs
      {...props}
      className={`${props.className || ''} ${stickyClassName}`.trim()}
      style={{ ...props.style, ...stickyStyle }}
      activeKey={activeKey}
      onSelect={(k) =>
        setSearchParams((searchParams) => {
          searchParams.set(tabName, k)
          return searchParams
        })
      }
      defaultActiveKey={defaultActiveKey}
    >
      {children}
    </Tabs>
  )
}
